import { scaleOrdinal, scaleLinear, scaleDiverging, scaleSequential } from "d3-scale";
import { formatFloat, formatInt } from "./map/language";
import { toObject, darken } from "./utils";
import { generateContinuousMapboxExpression } from "./map/mapbox_helpers";

// ///////////////////////////////////////////////////////////
// DATA
// ///////////////////////////////////////////////////////////

const generateSteps = (start, end, steps) => {
	if (steps < 2) {
		throw new Error("Steps must be at least 2 to include both start and end values.");
	}

	const stepSize = (end - start) / (steps - 1);
	const result = Array.from({ length: steps }, (_, i) => start + i * stepSize);

	return result;
};

export const colorScaleTransparent = scaleLinear()
	.domain(generateSteps(0, 100, 2))
	.range(["transparent", "transparent"])
	.clamp(true);

const colorScaleRelative = scaleLinear()
	.domain(generateSteps(0, 40, 5))
	.range(["#FFD3AB", "#FC9E89", "#EC6DA0", "#CA43B4", "#931A71"])
	.clamp(true);

// TF: workaround because they should all have the same color --> this was easier 🙈
const colorScaleAbsolute = scaleLinear().domain(generateSteps(0, 1242, 2)).range(["#931A71", "#931A71"]).clamp(true);

const colorScaleChange = scaleLinear()
	.domain([-2.0, -1.0, -0.5, -0.1, 0, 0.1, 0.5, 1.0, 2.0])
	.range(["#0C656A", "#1198A0", "#4BDED5", "#A3EFEB", "#ECE5EC", "#F6AFDE", "#ED8ADD", "#C72FAE", "#6F0050"])
	.clamp(true);

export const toggleButtons = [
	{
		id: "umwandlung_relativ",
		visType: "choropleth",
		label: "Umwandlungen pro 1000 Wohnungen",
		legendText: "Umwandlungen pro 1000 Wohnungen 2021/22",
		legendTickValues: [0, 20, 40],
		legendTickLabels: ["0", "20", ">40"],
		barMaxValue: 57,
		tooltipText: "Wohnungsumwandlungen<br>pro 1000 Wohnungen",
		colorScale: colorScaleRelative,
		color: "#D9488E",
		width: 285,
		formatValue: formatFloat({ adaptive: true, decimal: true, digits: 0 }),
		avgBerlin: 18.8169541698333274,
	},
	{
		id: "umwandlung_absolut",
		visType: "points",
		label: "absolut",
		legendText: "Wohnungsumwandlungen absolut 2021/22",
		barMaxValue: 4624,
		tooltipText: "Wohnungsumwandlungen<br>absolut",
		colorScale: colorScaleAbsolute,
		color: "#931A71",
		width: 90,
		formatValue: formatInt(),
		avgBerlin: 747.4833333333333,
	},
	{
		id: "change",
		visType: "choropleth",
		label: "Veränderung",
		legendText: "Veränderung von 2016-2022 auf 2021-2022",
		legendTickValues: [-2.0, 0, 2.0],
		legendTickLabels: ["weniger", "⌀", "mehr"],
		tooltipText: "Veränderung",
		colorScale: colorScaleChange,
		color: "#1198A0",
		width: 120,
		formatValue: formatFloat({
			multiplier: 100,
			signed: true,
			postfix: "%",
			prefix: "ca. ",
			adaptive: true,
			decimal: true,
			digits: 0,
		}),
		avgBerlin: 0.53,
	},
];

export const toggleButtonsById = toObject(toggleButtons, "id");

export const getTooltipSubtitleForChange = (change) => {
	const subject = "Wohnungsumwandlungen";
	const berlinAvg = toggleButtonsById.change.avgBerlin;
	const frmt = formatFloat({ multiplier: 100, signed: true, postfix: "%", digits: 0, adaptive: true });

	const thresholds = {
		muchmuch_below_zero: -0.6,
		much_below_zero: -0.3,
		below_zero: -0.03,
		around_zero: 0.03,
		above_zero: 0.35,
		similar: 0.7,
		more: 0.9,
		muchmore: 1.1,
	};

	if (change < thresholds.muchmuch_below_zero) {
		return `Die Anzahl der ${subject} nimmt sehr stark ab, während sie im Berliner Schnitt zunimmt (${frmt(
			berlinAvg
		)}).`;
	} else if (change < thresholds.much_below_zero) {
		return `Die Anzahl der ${subject} nimmt stark ab, während sie im Berliner Schnitt zunimmt (${frmt(berlinAvg)}).`;
	} else if (change < thresholds.below_zero) {
		return `Die Anzahl der ${subject} nimmt ab, während sie im Berliner Schnitt zunimmt (${frmt(berlinAvg)}).`;
	} else if (change < thresholds.around_zero) {
		return `Die Anzahl der ${subject} bleibt ähnlich, während sie im Berliner Schnitt zunimmt (${frmt(berlinAvg)}).`;
	} else if (change < thresholds.above_zero) {
		return `Die Anzahl der ${subject} nimmt weniger stark zu als im Berliner Schnitt (${frmt(berlinAvg)}).`;
	} else if (change < thresholds.similar) {
		return `Die Anzahl der ${subject} nimmt ähnlich stark zu wie im Berliner Schnitt (${frmt(berlinAvg)}).`;
	} else if (change < thresholds.more) {
		return `Die Anzahl der ${subject} nimmt stärker zu als im Berliner Schnitt (${frmt(berlinAvg)}).`;
	} else if (change < thresholds.muchmore) {
		return `Die Anzahl der ${subject} nimmt viel stärker zu als im Berliner Schnitt (${frmt(berlinAvg)}).`;
	} else {
		// muchmuchmore
		return `Die Anzahl der ${subject} nimmt sehr viel stärker zu als im Berliner Schnitt (${frmt(berlinAvg)}).`;
	}
};

toggleButtons.forEach((button) => {
	if (button.visType !== "points") return;

	const colors = button.colorScale.range();
	const domain = button.colorScale.domain();

	// get a mapbox expression for a continous color scale
	const colorExpression = generateContinuousMapboxExpression(colors, domain, button.id);

	const darkenedColors = colors.map((color) => darken(color, 0.3)); // Darken each color by 20%
	const darkenedExpression = generateContinuousMapboxExpression(darkenedColors, domain, button.id).slice(3);

	// Create the new structure with feature-state check
	button.mapboxColorExpression = [
		"case",
		["==", ["get", button.id], null],
		"#D9D9D9",
		[
			"case",
			["any", ["boolean", ["feature-state", "current"], false], ["boolean", ["feature-state", "hovered"], false]], // Check if 'current' or 'hovered' feature-state is true
			...darkenedExpression, // Apply darken if feature-state is true
			...colorExpression.slice(3), // Fallback to the original interpolate expression if feature-state is false
		],
	];
});

// export const datesStrings = ["2021", "2022"];

// export const dates = datesStrings.map((dateStr) => {
// 	return new Date(dateStr);
// });

// export const dateLookup = datesStrings.reduce((obj, dateStr, i) => {
// 	obj[dateStr] = dates[i];
// 	return obj;
// }, {});

export const datesStrings = ["avg_2016_2020", "avg_2021_2022"];

export const dateLookup = {
	avg_2016_2020: new Date(2016, 1, 1),
	avg_2021_2022: new Date(2021 - 1, 1),
};

// ///////////////////////////////////////////////////////////
// MAP
// ///////////////////////////////////////////////////////////

export const initialBounds = {
	desktop: [
		[12.829037255184318, 52.331871326934845],
		[14.102583501525999, 52.68177005131605],
	],
	tablet: [
		[13.211867193817284, 52.27179558551268],
		[13.899423327757603, 52.735101645391296],
	],
	mobile: [
		[13.050862498603664, 52.27129844848395],
		[13.79080489372484, 52.61263346358415],
	],
};

export const maxBounds = [
	[12.480232311625713, 51.94344715267028], // south west
	[14.381828464193688, 52.95668580240292], // north east
];

export const initialSelectedLocation = { lng: 13.305996739885767, lat: 52.513146990427195 };

export const baseChoroplethOpacity = 0.75;
export const baseChoroplethOpacityLuftbilder = 0.5;
export const basePointsOpacity = 1;
export const basePointsStrokeOpacity = 0.9;

export const bezirke = {
	"01": "Mitte",
	"02": "Friedrichshain-Kreuzberg",
	"03": "Pankow",
	"04": "Charlottenburg-Wilmersdorf",
	"05": "Spandau",
	"06": "Steglitz-Zehlendorf",
	"07": "Tempelhof-Schöneberg",
	"08": "Neukölln",
	"09": "Treptow-Köpenick",
	10: "Marzahn-Hellersdorf",
	11: "Lichtenberg",
	12: "Reinickendorf",
};

export const markerData = [{ n: "fernsehturm", lat: 52.52084869043665, lon: 13.409393064444167 }];
